import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Subida por partes de la preinscripción pública (spec preinscripcion-robusta, RF-020).
//
// Antes el formulario mandaba UNA petición de 5-20 MB con selfie, DNI y firma, y si no
// recibía respuesta no sabía si había llegado. Ahora cada imagen se sube sola, en cuanto
// se hace, y devuelve una referencia firmada (`ref`, opaca, válida 24 h). El envío final
// solo lleva texto y referencias.
//
// Cada hueco es una pequeña máquina de estados:
//   vacio → subiendo(%) → subida(ref) | error(codigo)
// El `File` se queda en memoria (NUNCA en sessionStorage) para que «Reintentar» no obligue
// a hacer la foto otra vez; tras una recarga solo sobrevive la `ref`.

export const KINDS = ['foto', 'dni_anverso', 'dni_reverso', 'firma'];

const SUBIDA_TIMEOUT_MS = 60000;
// Tres reintentos tras el primer intento, con espera creciente (base, 2×base, 4×base).
// Es un objeto para que los tests puedan acortar la espera sin fake timers.
export const MAX_REINTENTOS = 3;
export const TIEMPOS = { esperaBaseMs: 1000 };

const VACIO = { estado: 'vacio', pct: 0, ref: null, codigo: null, restaurada: false };

// Qué hacer ante un fallo. Reintentar solo sirve si el fallo es del CAMINO (sin
// respuesta, 5xx, 408, 429): un 400/413/422 dará lo mismo otra vez, y repetirlo solo
// retrasaría el mensaje que el socio necesita para hacer otra foto.
function clasificar(err) {
  const status = err?.response?.status;
  if (!status || status >= 500 || status === 408 || status === 429) {
    return { reintentable: true, codigo: 'red' };
  }
  if (status === 413) return { reintentable: false, codigo: 'grande' };
  if (status === 400) {
    const detalle = err.response?.data?.detail;
    return { reintentable: false, codigo: detalle === 'empty_file' ? 'vacio' : 'tipo' };
  }
  return { reintentable: false, codigo: 'otro' };
}

function slotsIniciales(refs) {
  const slots = {};
  KINDS.forEach((k) => {
    // Una ref restaurada del borrador: el fichero ya no está en memoria, pero el
    // servidor lo tiene. Se enseña «✓ subida» sin miniatura.
    slots[k] = refs?.[k]
      ? { ...VACIO, estado: 'subida', pct: 100, ref: refs[k], restaurada: true }
      : { ...VACIO };
  });
  return slots;
}

export default function usePreinscripcionSubidas(venueToken, refsIniciales) {
  const [slots, setSlots] = useState(() => slotsIniciales(refsIniciales));

  const tokenRef = useRef(venueToken);
  tokenRef.current = venueToken;
  const montadoRef = useRef(true);
  // Número de subida vigente por hueco: reemplazar la foto (o descartarla) invalida la
  // anterior aunque su respuesta llegue después.
  const seqRef = useRef({});
  const controllersRef = useRef({});
  const archivosRef = useRef({});
  // Espejo síncrono de las refs: tras un `await` el estado de React aún no se ha
  // repintado, y el envío final necesita la ref que acaba de salir.
  const refsRef = useRef(Object.fromEntries(
    KINDS.filter((k) => refsIniciales?.[k]).map((k) => [k, refsIniciales[k]]),
  ));

  useEffect(() => {
    montadoRef.current = true;
    const controllers = controllersRef.current;
    return () => {
      montadoRef.current = false;
      // Los temporizadores de espera NO se cancelan: bajo StrictMode el efecto se desmonta
      // y se vuelve a montar, y una espera cancelada dejaría la subida colgada para
      // siempre. Si el componente se desmonta de verdad, la espera vence sola y la subida
      // ve que ya no es vigente.
      Object.values(controllers).forEach((c) => c?.abort());
    };
  }, []);

  const poner = useCallback((kind, parcial) => {
    if (!montadoRef.current) return;
    setSlots((prev) => ({ ...prev, [kind]: { ...prev[kind], ...parcial } }));
  }, []);

  const esperar = useCallback((ms) => new Promise((resolve) => { setTimeout(resolve, ms); }), []);

  // Sube `archivo` al hueco. Devuelve la ref, o null si falló o quedó reemplazada.
  const ejecutar = useCallback(async (kind, archivo) => {
    const mio = (seqRef.current[kind] || 0) + 1;
    seqRef.current[kind] = mio;
    controllersRef.current[kind]?.abort();
    delete refsRef.current[kind];
    const vigente = () => montadoRef.current && seqRef.current[kind] === mio;
    poner(kind, { ...VACIO, estado: 'subiendo' });

    for (let intento = 0; ; intento += 1) {
      const ctl = new AbortController();
      controllersRef.current[kind] = ctl;
      let fallo = null;
      try {
        const form = new FormData();
        // El servidor saca la extensión del nombre: un Blob sin nombre (la firma) sale
        // como «blob» y se rechazaría.
        form.append('file', archivo, archivo.name || (kind === 'firma' ? 'firma.png' : 'foto.jpg'));
        const res = await axios.post(`/v1/pre-register/${tokenRef.current}/subidas/${kind}`, form, {
          timeout: SUBIDA_TIMEOUT_MS,
          signal: ctl.signal,
          onUploadProgress: (e) => {
            if (!vigente() || !e?.total) return;
            // 99 como mucho: «100 %» es cuando el servidor ha contestado con la ref.
            poner(kind, { pct: Math.min(99, Math.round((100 * e.loaded) / e.total)) });
          },
        });
        if (!vigente()) return null;
        const ref = res?.data?.ref;
        if (ref) {
          refsRef.current[kind] = ref;
          poner(kind, { estado: 'subida', pct: 100, ref, codigo: null, restaurada: false });
          return ref;
        }
        // 200 sin ref: el servidor o un proxy contestó algo que no es lo nuestro.
        fallo = { reintentable: true, codigo: 'red' };
      } catch (err) {
        if (!vigente()) return null;      // cancelada o reemplazada: ya no importa
        fallo = clasificar(err);
      }
      if (fallo.reintentable && intento < MAX_REINTENTOS) {
        await esperar(TIEMPOS.esperaBaseMs * 2 ** intento);
        if (!vigente()) return null;
        poner(kind, { pct: 0 });
        continue;
      }
      poner(kind, { estado: 'error', pct: 0, ref: null, codigo: fallo.codigo });
      return null;
    }
  }, [poner, esperar]);

  const subir = useCallback((kind, archivo) => {
    archivosRef.current[kind] = archivo;
    return ejecutar(kind, archivo);
  }, [ejecutar]);

  // «Reintentar»: vuelve a subir lo que ya está en memoria. Sin fichero (el hueco venía
  // de un borrador tras recargar) no hay nada que reenviar: se pide la foto otra vez.
  const reintentar = useCallback((kind) => {
    const archivo = archivosRef.current[kind];
    if (!archivo) {
      seqRef.current[kind] = (seqRef.current[kind] || 0) + 1;
      controllersRef.current[kind]?.abort();
      delete refsRef.current[kind];
      poner(kind, { estado: 'error', pct: 0, ref: null, codigo: 'sinArchivo', restaurada: false });
      return Promise.resolve(null);
    }
    return ejecutar(kind, archivo);
  }, [ejecutar, poner]);

  const descartar = useCallback((kind) => {
    seqRef.current[kind] = (seqRef.current[kind] || 0) + 1;
    controllersRef.current[kind]?.abort();
    delete archivosRef.current[kind];
    delete refsRef.current[kind];
    if (!montadoRef.current) return;
    setSlots((prev) => (prev[kind].estado === 'vacio' ? prev : { ...prev, [kind]: { ...VACIO } }));
  }, []);

  const reiniciar = useCallback(() => { KINDS.forEach(descartar); }, [descartar]);

  const refs = {};
  KINDS.forEach((k) => { if (slots[k].estado === 'subida') refs[k] = slots[k].ref; });

  return {
    slots,
    refs,
    // Las refs al instante, para leerlas justo después de un `await`.
    obtenerRefs: () => ({ ...refsRef.current }),
    subiendo: KINDS.filter((k) => slots[k].estado === 'subiendo'),
    conError: KINDS.filter((k) => slots[k].estado === 'error'),
    subir,
    reintentar,
    descartar,
    reiniciar,
  };
}
